import {createContainerElement, IContainerProps, TContainerElement} from '@platform';
import * as React from 'react';

import {TFocusableNodeType} from '../../../Tree/Constants/FocusableNodeType';
import ContainerContext from '../Contexts/ContainerContext';
import FocusableContext from '../Contexts/FocusableContext';
import IContainerContextProps from '../Contexts/Interfaces/IContainerContextProps';
import useFocus from '../Hooks/useFocus';

import ComponentDisplayName from './Constants/ComponentDisplayName';
import IFocusableComponentOptions from './Interfaces/IFocusableComponentOptions';
import TContainerPropsMap from './Types/TContainerPropsMap';
import extractElementAttributesFromProps from './Util/extractElementAttributesFromProps';
import validateProps from './Util/validateProps';

/**
 * An abstract "container" factory returning container components on which lists and grids are
 * built.
 *
 * Each container may contain zero or more focusable children, and is responsible for maintaining an
 * accurate ordering of children so that focus can be passed between them in the expected order.
 */

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createContainer = <T extends TFocusableNodeType, TContainerProps extends TContainerPropsMap[TFocusableNodeType]>(
    type: T
) => {
    const Container = React.forwardRef<TContainerElement, TContainerProps & IFocusableComponentOptions>(
        (props, forwardedRef: React.Ref<TContainerElement>) => {
            const focusableContext = React.useContext(FocusableContext);
            const isFirstRenderRef = React.useRef<boolean>(true);
            const hasNestedChangesRef = React.useRef<boolean>(false);

            hasNestedChangesRef.current = false;

            if (process.env.NODE_ENV !== 'production') {
                React.useLayoutEffect(() => validateProps(type, props), []);
            }

            const {focusableId, setFocusableElementRef, node} = useFocus({
                ...props,
                type: type as 'LEAF',
            });

            const unregisterFocusable = React.useCallback((id: string) => focusableContext.unregisterFocusable(id), []);

            const containerContextValue = React.useRef<IContainerContextProps>({
                parentContainerId: focusableId,
                parentContainerOrConnectorId: focusableId,
                registerFocusable: focusableContext.registerFocusable,
                unregisterFocusable,
            }).current;

            // Usage of `useLayoutEffect` over `useEffect` ensures that virtualised rails can change
            // focus before the browser paints changes to the DOM, so that all changes to
            // virtualised children *and* focus can be batched into a single DOM paint, avoiding a
            // flicker between traversal and focus change. NB: This results in a React warning about
            // "cascaded updates" in the chrome profiler but this is expected.

            React.useLayoutEffect(() => {
                if (node.isVirtualContainer && !node.delegateChangeFocus) {
                    focusableContext.changeVirtualFocus(focusableId, node.virtualFocusedIndex);
                }
            });

            const containerProps = props as IContainerProps;

            const {children, getFocusableId} = containerProps;

            if (isFirstRenderRef.current) {
                if (getFocusableId) getFocusableId(focusableId);

                isFirstRenderRef.current = false;
            }

            const elementAttributes = extractElementAttributesFromProps(props);

            return (
                <ContainerContext.Provider value={containerContextValue}>
                    {createContainerElement<typeof elementAttributes>({
                        children,
                        forwardedRef: forwardedRef as
                            | React.MutableRefObject<TContainerElement | null>
                            | ((instance: TContainerElement | null) => void),
                        elementAttributes,
                        setFocusableElementRef,
                    })}
                </ContainerContext.Provider>
            );
        }
    );

    Container.displayName = ComponentDisplayName[type];

    return Container;
};

export default createContainer;
